/**
 * Procedural flagstone floor — generates canvas-based albedo + normal +
 * roughness maps at mount, applied to a single floor plane. Replaces the
 * old flat-colored plane.
 *
 * Each "tile" in the canvas is one flagstone. Tiles have:
 *   - slight per-tile hue/lightness variation (deterministic from a seeded LCG)
 *   - dirt/noise speckles
 *   - inner-edge darkening that reads as recession
 * Plus a matching normal map with beveled edges so the grout grooves catch
 * the directional + torch lights.
 *
 * Texture canvas is 512×512 covering 4×4 flagstones; the resulting texture
 * tiles across the floor with `repeat` so the floor reads as ~28×34 stones
 * total without any visible seam pattern.
 */
import { useEffect, useMemo } from "react";
import * as THREE from "three";

interface ProceduralStoneFloorProps {
  width: number;
  depth: number;
  /** World position of the floor's center. */
  position?: [number, number, number];
  /** How many copies of the 4×4-stone texture tile across the floor. */
  repeat?: [number, number];
}

export function ProceduralStoneFloor({
  width,
  depth,
  position = [0, 0.01, 0],
  repeat = [7, 8.5],
}: ProceduralStoneFloorProps): JSX.Element {
  const { albedo, normal, rough } = useMemo(() => buildFloorTextures(512, 4, 4), []);

  useEffect(() => {
    for (const t of [albedo, normal, rough]) {
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(repeat[0], repeat[1]);
      t.anisotropy = 8;
      t.needsUpdate = true;
    }
    albedo.colorSpace = THREE.SRGBColorSpace;
    return () => {
      albedo.dispose();
      normal.dispose();
      rough.dispose();
    };
  }, [albedo, normal, rough, repeat]);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={position} receiveShadow>
      <planeGeometry args={[width, depth]} />
      <meshStandardMaterial
        map={albedo}
        normalMap={normal}
        normalScale={new THREE.Vector2(0.9, 0.9)}
        roughnessMap={rough}
        roughness={1.0}
        color="#a8957a"
      />
    </mesh>
  );
}

interface FloorTextures {
  albedo: THREE.CanvasTexture;
  normal: THREE.CanvasTexture;
  rough: THREE.CanvasTexture;
}

function buildFloorTextures(size: number, tilesU: number, tilesV: number): FloorTextures {
  const tileW = size / tilesU;
  const tileH = size / tilesV;
  const groutPx = Math.max(2, Math.floor(size / 96));
  const bevelPx = Math.max(4, Math.floor(tileW * 0.12));

  // Deterministic PRNG so the floor looks the same every load.
  let seed = 0xc0ffee;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };

  // === Albedo ===
  const albCanvas = document.createElement("canvas");
  albCanvas.width = size;
  albCanvas.height = size;
  const ac = albCanvas.getContext("2d")!;

  // Grout base — dark recess color fills the entire canvas first.
  ac.fillStyle = "#2a221a";
  ac.fillRect(0, 0, size, size);

  // Each flagstone: a slightly varied warm-tan rectangle with internal noise.
  for (let j = 0; j < tilesV; j++) {
    for (let i = 0; i < tilesU; i++) {
      const x = i * tileW + groutPx;
      const y = j * tileH + groutPx;
      const w = tileW - groutPx * 2;
      const h = tileH - groutPx * 2;

      const hue = 28 + (rand() - 0.5) * 6;
      const sat = 18 + rand() * 10;
      const light = 36 + rand() * 16;
      const tileColor = `hsl(${hue}, ${sat}%, ${light}%)`;

      ac.fillStyle = tileColor;
      ac.fillRect(x, y, w, h);

      // Inner-edge darkening — fakes a chamfered recess so the bevel reads
      // even at grazing camera angles.
      const grad = ac.createLinearGradient(x, y, x + w, y);
      grad.addColorStop(0, "rgba(0,0,0,0.35)");
      grad.addColorStop(0.15, "rgba(0,0,0,0)");
      grad.addColorStop(0.85, "rgba(0,0,0,0)");
      grad.addColorStop(1, "rgba(0,0,0,0.35)");
      ac.fillStyle = grad;
      ac.fillRect(x, y, w, h);
      const gradY = ac.createLinearGradient(x, y, x, y + h);
      gradY.addColorStop(0, "rgba(0,0,0,0.35)");
      gradY.addColorStop(0.15, "rgba(0,0,0,0)");
      gradY.addColorStop(0.85, "rgba(0,0,0,0)");
      gradY.addColorStop(1, "rgba(0,0,0,0.35)");
      ac.fillStyle = gradY;
      ac.fillRect(x, y, w, h);

      // Dirt + speckle noise — alternating dark and light flecks for "stone".
      ac.save();
      ac.beginPath();
      ac.rect(x, y, w, h);
      ac.clip();
      for (let n = 0; n < 90; n++) {
        const nx = x + rand() * w;
        const ny = y + rand() * h;
        const nr = 1 + rand() * 4;
        const dark = rand() > 0.5;
        ac.globalAlpha = 0.06 + rand() * 0.08;
        ac.fillStyle = dark ? "#1a1208" : "#fff0d8";
        ac.beginPath();
        ac.arc(nx, ny, nr, 0, Math.PI * 2);
        ac.fill();
      }
      ac.restore();
      ac.globalAlpha = 1;
    }
  }

  // === Normal map ===
  // Encode per-pixel tangent-space normals. Flat-up is rgb(128,128,255).
  // Inside the bevel zone, tilt the normal away from the tile centre so the
  // bevel slopes down into the grout.
  const normCanvas = document.createElement("canvas");
  normCanvas.width = size;
  normCanvas.height = size;
  const nc = normCanvas.getContext("2d")!;
  const nImg = nc.createImageData(size, size);
  const nData = nImg.data;

  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const tileCol = Math.floor(px / tileW);
      const tileRow = Math.floor(py / tileH);
      const inTileX = px - tileCol * tileW;
      const inTileY = py - tileRow * tileH;

      let nx = 0;
      let ny = 0;
      let nz = 1;

      const inGrout =
        inTileX < groutPx ||
        inTileX >= tileW - groutPx ||
        inTileY < groutPx ||
        inTileY >= tileH - groutPx;

      if (!inGrout) {
        const distLeft = inTileX - groutPx;
        const distRight = tileW - groutPx - inTileX;
        const distTop = inTileY - groutPx;
        const distBot = tileH - groutPx - inTileY;
        const tiltX =
          Math.max(0, 1 - distLeft / bevelPx) - Math.max(0, 1 - distRight / bevelPx);
        const tiltY =
          Math.max(0, 1 - distTop / bevelPx) - Math.max(0, 1 - distBot / bevelPx);
        const strength = 0.65;
        nx = tiltX * strength;
        ny = tiltY * strength;
        const mag2 = nx * nx + ny * ny;
        nz = mag2 < 1 ? Math.sqrt(1 - mag2) : 0;
      }

      const idx = (py * size + px) * 4;
      nData[idx + 0] = Math.round((nx * 0.5 + 0.5) * 255);
      nData[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      nData[idx + 2] = Math.round(nz * 255);
      nData[idx + 3] = 255;
    }
  }
  nc.putImageData(nImg, 0, 0);

  // === Roughness ===
  // Grout is darker / rougher; tile interior is slightly polished, with
  // per-tile variation so reflections aren't uniform.
  const roughCanvas = document.createElement("canvas");
  roughCanvas.width = size;
  roughCanvas.height = size;
  const rc = roughCanvas.getContext("2d")!;
  rc.fillStyle = "#dddddd";
  rc.fillRect(0, 0, size, size);

  for (let j = 0; j < tilesV; j++) {
    for (let i = 0; i < tilesU; i++) {
      const x = i * tileW + groutPx;
      const y = j * tileH + groutPx;
      const w = tileW - groutPx * 2;
      const h = tileH - groutPx * 2;
      const polish = 110 + Math.floor(rand() * 60);
      const hex = polish.toString(16).padStart(2, "0");
      rc.fillStyle = `#${hex}${hex}${hex}`;
      rc.fillRect(x, y, w, h);
    }
  }

  return {
    albedo: new THREE.CanvasTexture(albCanvas),
    normal: new THREE.CanvasTexture(normCanvas),
    rough: new THREE.CanvasTexture(roughCanvas),
  };
}
